import React, { useEffect, useRef, useCallback, useState } from 'react';
import Select, {
  FilterOptionOption,
  GroupBase,
  components,
  SingleValue,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import styles from './Dropdown.module.scss';
import { useFieldValidity } from '../../../pages/ApplicationV3/hooks/useFieldValidity';
import { Controller, useFormContext } from 'react-hook-form';
import classNames from 'classnames';
import { useDebouncer } from '../../../hooks';

export interface Option {
  value: string;
  label: string;
  subLabel?: string;
  isNotFoundOption?: boolean;
}

export const DID_NOT_FIND_VALUE = 'did-not-find-option';

export interface GroupedOption extends GroupBase<Option> {
  label: string;
  options: Option[];
}

type Props = {
  options?: (Option | GroupedOption)[];
  name: string;
  label: string;
  onChange?: (option: Option | null) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  autoOpen?: boolean;
  fixedHeaderLabel?: string;
  dataCy?: string;
  placeholder?: string;
  loadOptions?: (inputValue: string) => Promise<Option[]>;
  debounceMs?: number;
  minSearchLength?: number;
  showNotFoundOption?: boolean;
  notFoundOption?: Option;
  onNotFoundClick?: (searchTerm: string) => void;
  value?: Option | null;
  defaultValue?: Option | null;
  isSearchable?: boolean;
};

const CustomMenuList = (props: any) => {
  const {
    children,
    showNotFoundOption,
    notFoundOption,
    inputValue,
    onNotFoundClick,
  } = props;

  return (
    <components.MenuList {...props}>
      {children}
      {showNotFoundOption && notFoundOption && (
        <div
          data-cy="not-found-option"
          onClick={() => onNotFoundClick(inputValue)}
          className={styles.notFoundOptionWrapper}
        >
          <div className={styles.optionContainer}>
            <p className={styles.optionLabel}>{notFoundOption.label}</p>
            {notFoundOption.subLabel && (
              <span className={styles.optionSubLabel}>
                {notFoundOption.subLabel}
              </span>
            )}
          </div>
        </div>
      )}
    </components.MenuList>
  );
};

const formatGroupLabel = (data: GroupedOption) => (
  <div className={styles.groupContainer}>
    <p>{data.label}</p>
  </div>
);

const formatOptionLabel = (
  option: Option,
  { context }: { context: 'menu' | 'value' }
) => {
  if (context === 'menu') {
    return (
      <div className={styles.optionContainer}>
        <p className={styles.optionLabel}>{option.label}</p>
        {option.subLabel && (
          <span className={styles.optionSubLabel}>{option.subLabel}</span>
        )}
      </div>
    );
  }

  return (
    <div className={styles.selectedOptionContainer}>
      <p className={styles.optionLabel}>{option.label}</p>
      {option.subLabel && (
        <span className={styles.optionSubLabel}>{option.subLabel}</span>
      )}
    </div>
  );
};

const CustomMenu = (props: any) => (
  <components.Menu {...props}>
    {props.fixedHeaderLabel && (
      <div className={styles.fixedHeader} data-cy="fixed-header">
        {props.fixedHeaderLabel}
      </div>
    )}
    {props.children}
  </components.Menu>
);

const filterOption = (
  option: FilterOptionOption<Option>,
  inputValue: string
) => {
  const searchText = inputValue.trim().toLowerCase();
  return (
    option.label.toLowerCase().includes(searchText) ||
    (option.data.subLabel !== undefined &&
      option.data.subLabel.toLowerCase().includes(searchText))
  );
};

const Dropdown = ({
  options,
  onChange,
  name,
  label,
  isLoading,
  isDisabled,
  autoOpen,
  fixedHeaderLabel,
  dataCy,
  placeholder,
  loadOptions,
  debounceMs = 500,
  minSearchLength = 3,
  onNotFoundClick,
  notFoundOption,
  showNotFoundOption = false,
  value,
  defaultValue,
  isSearchable: isSearchableProp = true,
}: Props) => {
  const { control } = useFormContext();
  const { isValid, invalid, error } = useFieldValidity(name);
  const selectRef = useRef<any>(null);
  const [selectedAsyncOption, setSelectedAsyncOption] = useState<Option | null>(
    defaultValue || null
  );
  const [inputValue, setInputValue] = useState('');
  const [menuIsOpen, setMenuIsOpen] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (value !== undefined && loadOptions) {
      setSelectedAsyncOption(value);
    }
  }, [value, loadOptions]);

  useEffect(() => {
    if (autoOpen === true) {
      setMenuIsOpen(true);
    } else {
      setMenuIsOpen(undefined);
    }
  }, [autoOpen]);

  const handleNotFoundClick = useCallback(
    (searchTerm: string) => {
      setMenuIsOpen(false);

      if (selectRef.current) {
        selectRef.current.blur();
      }

      if (loadOptions) {
        setSelectedAsyncOption(notFoundOption || null);
      }

      onNotFoundClick?.(searchTerm);
      onChange?.(notFoundOption || null);
    },
    [loadOptions, onChange, onNotFoundClick, notFoundOption]
  );

  const handleLoadOptions = useCallback(
    (inputValue: string, callback: (options: Option[]) => void) => {
      if (!loadOptions) {
        callback([]);
        return;
      }
      const searchValue = inputValue.trim();
      if (searchValue.length < minSearchLength) {
        callback([]);
        return;
      }

      loadOptions(searchValue)
        .then((options) => callback(options))
        .catch((error) => {
          console.error('Error loading options:', error);
          callback([]);
        });
    },
    [loadOptions, minSearchLength]
  );

  const debouncedLoadOptions = useDebouncer(handleLoadOptions, debounceMs);

  const commonProps = {
    ref: selectRef,
    name,
    formatOptionLabel,
    formatGroupLabel,
    placeholder: placeholder ?? 'Välj ett alternativ',
    isDisabled,
    menuIsOpen: menuIsOpen,
    openMenuOnFocus: true,
    onMenuOpen: () => setMenuIsOpen(true),
    onMenuClose: () => setMenuIsOpen(false),
    onInputChange: (value: string) => setInputValue(value),
    className: classNames(styles.border, {
      [styles.isValid]: isValid,
      [styles.isInvalid]: invalid,
    }),
    classNamePrefix: 'react-select',
    components: {
      ...(fixedHeaderLabel
        ? {
            Menu: (props: any) => (
              <CustomMenu {...props} fixedHeaderLabel={fixedHeaderLabel} />
            ),
          }
        : {}),
      ...(showNotFoundOption
        ? {
            MenuList: (props: any) => (
              <CustomMenuList
                {...props}
                showNotFoundOption={showNotFoundOption}
                notFoundOption={notFoundOption}
                inputValue={inputValue}
                onNotFoundClick={handleNotFoundClick}
              />
            ),
          }
        : {}),
    },
  };
  return (
    <div data-cy={dataCy ?? 'dropdown-component'}>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const selectedOption = options
            ? options
                .flatMap((o) => ('options' in o ? o.options : [o]))
                .find((o) => o.value === field.value) || null
            : null;
          if (loadOptions) {
            return (
              <div className={styles.container}>
                <label
                  htmlFor={name}
                  data-cy={'dropdown-label'}
                  className={styles.label}
                >
                  {label}
                </label>
                <AsyncSelect
                  {...commonProps}
                  value={value !== undefined ? value : selectedAsyncOption}
                  defaultValue={defaultValue}
                  loadOptions={debouncedLoadOptions}
                  defaultOptions={false}
                  onChange={(option: SingleValue<Option>) => {
                    setSelectedAsyncOption(option);
                    if (!option) {
                      field.onChange(undefined);
                    } else {
                      field.onChange(option?.value || '');
                    }
                    onChange?.(option ?? null);
                  }}
                  onBlur={() => {
                    field.onBlur();
                  }}
                  styles={{
                    container: (base) => ({
                      ...base,
                    }),
                    valueContainer: (baseStyles, state) => ({
                      ...baseStyles,
                      padding: '0 1.625rem',
                    }),
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      border: 'none',
                      boxShadow: 'none',
                    }),
                    indicatorSeparator: (base) => ({
                      ...base,
                      color: 'inherit',
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: 'inherit',
                      paddingRight: '1.625rem',
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      backgroundColor: state.isSelected
                        ? '#f0f0f0'
                        : state.isFocused
                          ? '#e0e0e0'
                          : baseStyles.backgroundColor,
                      ':active': {
                        backgroundColor: '#d0d0d0', // Mörkare grå när du håller ner musknappen
                      },
                      cursor: 'pointer',
                    }),
                  }}
                  isClearable
                  isSearchable
                  noOptionsMessage={({ inputValue }) =>
                    inputValue.length < minSearchLength
                      ? `Skriv minst ${minSearchLength} tecken för att söka`
                      : 'Inga alternativ hittades'
                  }
                />
                {error && (
                  <span className={styles.errorText}>{error.message}</span>
                )}
              </div>
            );
          }

          // Use regular Select for static options
          return (
            <div className={styles.container}>
              <label
                className={styles.label}
                htmlFor={name}
                data-cy={'dropdown-label'}
              >
                {label}
              </label>
              <Select
                {...commonProps}
                {...field}
                options={options}
                value={value !== undefined ? value : selectedOption}
                defaultValue={defaultValue}
                onChange={(option: SingleValue<Option>) => {
                  if (!option) {
                    field.onChange(undefined);
                  } else {
                    field.onChange(option?.value || '');
                  }
                  onChange?.(option ?? null);
                }}
                onBlur={() => {
                  field.onBlur();
                }}
                styles={{
                  container: (base) => ({
                    ...base,
                  }),
                  valueContainer: (baseStyles, state) => ({
                    ...baseStyles,
                    padding: '0 1.625rem',
                  }),
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    border: 'none',
                    boxShadow: 'none',
                  }),
                  indicatorSeparator: (base) => ({
                    ...base,
                    color: 'inherit',
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    color: 'inherit',
                    paddingRight: '1.625rem',
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: state.isSelected
                      ? '#f0f0f0'
                      : state.isFocused
                        ? '#e0e0e0'
                        : baseStyles.backgroundColor,
                    ':active': {
                      backgroundColor: '#d0d0d0', // Mörkare grå när du håller ner musknappen
                    },
                    cursor: 'pointer',
                  }),
                }}
                isClearable
                isSearchable={isSearchableProp}
                isLoading={isLoading}
                filterOption={filterOption}
                noOptionsMessage={({ inputValue }) =>
                  'Inga alternativ hittades'
                }
              />
              {error && (
                <span className={styles.errorText}>{error.message}</span>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};

export default Dropdown;
